"use client";

import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { CircleSlash, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import type { CSSProperties, RefObject } from "react";
import cn from "twsx";
import styles from "./List.module.css";

export function useListViewportClientWidth(ref: RefObject<HTMLDivElement>) {
  const [clientWidth, setClientWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const updateClientWidth = () => {
      setClientWidth(element.clientWidth);
    };

    updateClientWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateClientWidth);

      return () => {
        window.removeEventListener("resize", updateClientWidth);
      };
    }

    const resizeObserver = new ResizeObserver(updateClientWidth);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref]);

  return clientWidth;
}

export function ListSurfaceState({
  className,
  isRetrying = false,
  onRetry,
  state,
  viewportClientWidth,
}: {
  className?: string;
  isRetrying?: boolean;
  onRetry?: () => void;
  state: "empty" | "error" | "loading";
  /** Measured scroll viewport width, for lists that render this outside FilterList's grid. */
  viewportClientWidth?: number | null;
}) {
  return (
    <div
      className={cn("flex flex-col items-center justify-center py-20", styles.viewportCentered, className)}
      style={viewportClientWidth ? { "--list-viewport-width": `${viewportClientWidth}px` } as CSSProperties : undefined}
    >
      {state === "empty"
        ? (
          <EmptyState
            description="No results found for your search"
            icon={CircleSlash}
            title="No results"
          />
        )
        : state === "error"
        ? (
          <EmptyState
            tone="danger"
            description="The list could not be loaded. Try again without losing your filters."
            icon={CircleSlash}
            title="Couldn&apos;t load results"
            action={onRetry
              ? (
                <Button
                  disabled={isRetrying}
                  onClick={onRetry}
                  variant="danger"
                >
                  <RotateCcw />
                  {isRetrying ? "Retrying..." : "Retry"}
                </Button>
              )
              : undefined}
          />
        )
        : <Spinner size="md" tone="faint" />}
    </div>
  );
}
